import { exceptionName, makeError } from './exception';

// 'AbortError' is the name of default Error reason when
// aborting an AbortController, so we use that name so that such
// errors pass isAbortError.
export const ABORT_ERROR = 'AbortError';
export function isAbortError(err: unknown) {
  return exceptionName(err) === ABORT_ERROR;
}

export function isCanceledError(err: unknown) {
  const message = err instanceof Error ? err.message : String(err);
  const lowerMessage = message.toLowerCase();
  return lowerMessage.includes('cancel') || lowerMessage === 'canceled';
}

/**
 * Races `func()` against the abort signal: rejects with an `AbortError` when
 * the signal aborts first (also when it is already aborted — the 'abort'
 * event never fires on an already-aborted signal). `func()` is invoked
 * either way and its eventual settlement is always observed, so losing the
 * race cannot produce an unhandled rejection.
 */
export function abortable<T>(func: () => Promise<T>, signal: AbortSignal): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => {
      reject(makeError('Aborted', ABORT_ERROR, {
        cause: signal.reason,
      }));
    };
    if (signal.aborted) {
      onAbort();
    } else {
      signal.addEventListener('abort', onAbort);
    }
    func()
      .then(resolve, reject)
      .finally(() => signal.removeEventListener('abort', onAbort));
  });
}
